import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { map, mergeMap, tap } from 'rxjs/operators';
import { Sale } from '../models/sale.model';
import { FormGroup, FormControl } from '@angular/forms';
import { CDCService, IClientSearchParams } from './cdc.service';
import { MsAuthService } from './ms-auth.service';
import { Client } from '../models/client.model';

export enum TransactionSelections {
  all = "ALL",
  ecomm = "ECOMM",
  retail = "RETAIL",
}

export enum ReasonCodeSelections {
  wrongAddr = "Wrong Shipping Address",
  wrongItems = "Wrong Items(s)",
  wrongPayment = "Wrong Payment Method",
  changedMind = "Changed  Mind",
  foundAlternative = "Found Alternative Elsewhere",
  mistake = "Ordered By Mistake",
  other = "Other"
}

export interface ISaleHistoryWithMeta {
  data: ISaleHistory[];
  next?:string,
  prev?:string,
  total?:number
}

export interface ISaleHistory {
  id: string;
  type: string;
  orderType?: string;
  status: string;
  initiatingLocation: string;
  pickupLocation: string;
  registerId: string;
  returns: ISalesHistoryReturnItem[];
  date: string;
  customerId: string;
  saleChId?: string;
  order?: IOrder;
  transactions?: ITransaction[];
}

export interface ISalesHistoryReturnItem {
  id: string;
  name: string;
  quantity: number;
  color: string;
  size: string;
}

export interface IOrder {
  type: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  status: string;
  orderType: string;
  orderId: string;
  saleChId: string;
  isRetail: boolean;
  orderDate: string;
  orderTimestamp: string;
  totalCount: number;
  clientId: string;
  initiatingStore: string;
  pickupStore: string;
  serviceLevel: string;
  billingAddress?: IOrderAddress;
  shippingAddress?: IOrderAddress;
  originalPrice: number;
  discount: number;
  subtotal: number;
  tax: number;
  shippingFee: number;
  total: number;
  currency: string;
  items: IOrderItem[];
  saleAssociateId?: string;
  cashierId?: string;
  returnedOrders?: IOrder[];
  payments: IPayment[];
  registerId: string;
  deliveryBlock: string;
  siteSourceName: string;
  siteSourceId: string;
}

export interface IOrderAddress {
  addressTypeCode: string;
  addressTypeName: string;
  firstName: string;
  lastName: string;
  line1: string;
  line2: string;
  line3: string;
  cityName: string;
  provinceCode: string;
  postalCode: string;
  countryCode: string;
  phoneNumber: string;
  email: string;
}

export interface IOrderItem {
  name: string;
  brand: string;
  id: string;
  lineItem: string;
  productId: string;
  color: string;
  size: string;
  quantity: number;
  priceList: number;
  price: number;
  trackingNumbers: string[];
  fulfillingStore: string;
  backOrderDate: string;
  status: string;
  upc: string;
  history: IFulfillmentStatus[];
  subTotal: number;
  tax: number;
  total: number;
  returnReferenceLine: string;
  processedDate: string;
  backOrderFlag: boolean;
  merchandiseCategoryCode: string;
  collection?: string;
  img?: string;
  onlinePrice?: number;
}

export interface IFulfillmentStatus {
  statusCode: string;
  statusName: string;
  fulfillmentLocationId: string;
  sequence: number;
  rejectionReasonCode?: string;
  userId: string;
  timestamp: string;
}

export interface IPayment {
  type: string;
  id: string;
  attributes: IPaymentData;
}

export interface IPaymentData {
  paymentType: string;
  paymentAmount: number;
  paymentCardHolder: string;
  paymentCardNumber: string;
  paypalAttributes?: IPaypalAttributes;
}

export interface IPaypalAttributes {
  paypalEmail: string,
  paypalRequestId: string;
  paypalRequestToken: string;
  paypalAuthTransactionId: string;
  paypalOSRequestId: string;
  paypalOSRequestToken: string;
  paypalOSTransactionId: string;
}

export interface ITransaction{
  id: string;
  locationId: string;
  locationName: string;
  registerId: string;
  cashierId: string;
  associateId: string;
  currency: string;
  name: string;
  email: string;
  phone?: string;
  items: ITransactionItem[];
  payments: ITransactionPayment[];
  discount: number;
  originalPrice: number;
  subtotal: number;
  tax: number;
  shippingFee: number;
  total: number;
  transactionDate: string;
  transactionTimestamp: string;
  clientId: string;
  typeCode: string;
  typeName: string;
  returnItems: ITransactionItem[];
}

export interface ITransactionItem{
  lineItem: string;
  productId: string;
  genericId: string;
  colour: string;
  size: string;
  upc: string;
  serialId: string;
  quantity: number;
  initialPrice: number;
  salePrice: number;
  name: string;
  statusId: string;
  status: string;
  ecommId: string;
  shippingName?: string;
  shippingPhone?: string;
  cityName?: string;
  provinceCode?: string;
  postalCode?: string;
  countryCode?: string;
  shippingStreetAddress?: string;
  referenceId: string;
  img?: string;
  brand?:string;
  collection?: string;
  onlinePrice?: number;
}

export interface ITransactionPayment {
  itemId: string;
  typeCode: string;
  typeName: string;
  amount: number;
  currency: string;
  tenderId: string;
  referenceId: string;
  enhancements: ITransactionEnhancement[];
}

export interface ITransactionEnhancement {
  group: string;
  key: string;
  value: string;
}


@Injectable({
  providedIn: 'root',
})
export class SaleService {
  private _host = `${environment.apigee.host}/concierge/customers/sales`;
  private _next: string | undefined;
  private _prev: string | undefined;
  private _sales: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  public sales$: Observable<any[]> = this._sales.asObservable();
  public sales: Sale[] = [];
  public articleId = '';
  public salesListYAxis = 0;
  public totalSales: number = 0;
  public customerIds: string[] = [];
  public dateRange = new FormGroup({start: new FormControl, end: new FormControl});
  public selectedSale: Sale;

  constructor(
    private _httpClient: HttpClient,
    private _translate: TranslateService,
    private _cdcService: CDCService,
    private _msAuthService: MsAuthService
  ) {}

  public getIniitalSaleHistory(): Observable<Sale[]> {
    const url = `${this._host}/customer`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    let body = JSON.stringify({
      customerIds: this.customerIds,
      start: this.dateRange.value.start.toISOString().split('T')[0],
      end: this.dateRange.value.end.toISOString().split('T')[0],
      locale: this._locale
    });
    return this._httpClient.post<ISaleHistoryWithMeta>(url, body, httpOptions).pipe(map((result) => {
      this._next = result.next;
      this._prev = result.prev;
      this.totalSales = result.total;
      this.sales = result.data.map((val) => {
        const client = this._cdcService.clients.find((client) =>
            client.ids.some((id) => id === val.customerId)
        );
        return new Sale(val, client);
      });
      this._sales.next(this.sales);
      return this.sales;
    }));
  }

  public getNextSaleHistory(): Observable<Sale[]> {
    const url = `${this._host}/customer/next/${this._next}?locale=${this._locale}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };

    return this._httpClient.get<ISaleHistoryWithMeta>(url, httpOptions).pipe(map((result) => {
      this._next = result.next;
      this._prev = result.prev;
      let newSales = result.data.map((val) => {
        const client = this._cdcService.clients.find((client) =>
            client.ids.some((id) => id === val.customerId)
        );
        return new Sale(val, client);
      });
      this.sales = this.sales.concat(newSales);
      this._sales.next(this.sales);
      return newSales;
    }));
  }

  //Not sure if this will ever be used
  public getPrevSaleHistory(): Observable<Sale[]> {
    const url = `${this._host}/customer/previous/${this._prev}?locale=${this._locale}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    return this._httpClient.get<ISaleHistoryWithMeta>(url, httpOptions).pipe(map((result) => {
      this._next = result.next;
      this._prev = result.prev;
      let newSales = result.data.map((val) => {
        const client = this._cdcService.clients.find((client) =>
            client.ids.some((id) => id === val.customerId)
        );
        return new Sale(val, client);
      });
      this.sales = newSales.concat(this.sales);
      this._sales.next(this.sales);
      return newSales;
    }));
  }

  public getOrderById(orderId: string): Observable<Sale[]> {
    const url = `${this._host}/order/${orderId}?locale=${this._locale}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    return this._httpClient.get<ISaleHistory[]>(url, httpOptions).pipe(mergeMap((result: ISaleHistory[]) => {
      const obs = result.map((val: ISaleHistory) => {
        const searchQuery: IClientSearchParams = {
          fields: ['data', 'profile'],
          customerId: val.customerId,
        };
        if (this._msAuthService.isLoggedIn()) {
          //Should only have one client with specific id given.
          return this._cdcService.clientSearch(searchQuery).pipe(map((clients: Client[]) => {
            let sale = new Sale(val, clients[0]);
            if(result.length === 1) {
              this.selectedSale = sale;
            }
            return sale;
          }));
        } else  {
          let sale = new Sale(val);
          if(result.length === 1) {
            this.selectedSale = sale;
          }
          return of(sale);
        }
      });
      if(obs.length === 0) {
        return of([]);
      }
      return forkJoin(obs).pipe(tap((sales: Sale[]) => {
        this.sales = sales;
        this._sales.next(sales);
      }));
    }));
  }

  public getTransactionByOrderId(orderId: string): Observable<Sale> {
    const url = `${this._host}/transaction/${orderId}?locale=${this._locale}`;
    return this._getTransaction(url);
  }

  public getTransactionById(transactionId: string, registerId: string, locationId: string, transactionDate: string): Observable<Sale> {
    const url = `${this._host}/transaction/${transactionId}/${registerId}/${locationId}/${transactionDate}?locale=${this._locale}`;
    return this._getTransaction(url);
  }

  private _getTransaction(url: string): Observable<Sale> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    return this._httpClient.get<ISaleHistory>(url, httpOptions).pipe(mergeMap((result: ISaleHistory) => {
      if (this._msAuthService.isLoggedIn() && result.customerId) {
        const searchQuery: IClientSearchParams = {
          fields: ['data', 'profile'],
          customerId: result.customerId,
        };
        //Should only have one client with specific id given.
        return this._cdcService.clientSearch(searchQuery).pipe(map((clients: Client[]) => {
          return new Sale(result, clients[0]);
        }));
      } else  {
        return of(new Sale(result));
      }
    }));
  }

  public getInitialArticleById(genericId: string, locationId: string, transactionDate: string) {
    const url = `${this._host}/article/${genericId}/${locationId}/${transactionDate}?locale=${this._locale}`;
    this.articleId = genericId;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    return this._httpClient.get<ISaleHistoryWithMeta>(url, httpOptions).pipe(mergeMap((result) => {
      this._next = result.next;
      this._prev = result.prev;
      this.totalSales = result.total;
      const obs = result.data.map((val) => {
        const searchQuery: IClientSearchParams = {
          fields: ['data', 'profile'],
          customerId: val.customerId,
        };
        if (this._msAuthService.isLoggedIn()) {
          //Should only have one client with specific id given.
          return this._cdcService.clientSearch(searchQuery).pipe(map((clients: Client[]) => {
            return new Sale(val, clients[0]);
          }));
        } else  {
          return of(new Sale(val));
        }
      });
      if(obs.length === 0) {
        return of([]);
      }
      return forkJoin(obs).pipe(map((result) => {
        this.sales = result;
        this._sales.next(this.sales);
        return result;
      }));
    }));
  }

  public getNextArticles() {
    const url = `${this._host}/article/${this._next}?locale=${this._locale}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    return this._httpClient.get<ISaleHistoryWithMeta>(url, httpOptions).pipe(mergeMap((result) => {
      this._next = result.next;
      this._prev = result.prev;
      const obs = result.data.map((val) => {
        const searchQuery: IClientSearchParams = {
          fields: ['data', 'profile'],
          customerId: val.customerId,
        };
        if (this._msAuthService.isLoggedIn()) {
          //Should only have one client with specific id given.
          return this._cdcService.clientSearch(searchQuery).pipe(map((clients: Client[]) => {
            return new Sale(val, clients[0]);
          }));
        } else  {
          return of(new Sale(val));
        }
      });
      if(obs.length === 0) {
        return of([]);
      }
      return forkJoin(obs).pipe(map((result) => {
        this.sales = this.sales.concat(result);
        this._sales.next(this.sales);
        return result;
      }))
    }));
  }

  //Not sure if we will ever use this
  public getPrevArticles() {
    const url = `${this._host}/article/${this._prev}?locale=${this._locale}`;
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type':  'application/json'
      })
    };
    return this._httpClient.get<ISaleHistoryWithMeta>(url, httpOptions).pipe(mergeMap((result) => {
      this._next = result.next;
      this._prev = result.prev;
      const obs = result.data.map((val) => {
        const searchQuery: IClientSearchParams = {
          fields: ['data', 'profile'],
          customerId: val.customerId,
        };
        if (this._msAuthService.isLoggedIn()) {
          //Should only have one client with specific id given.
          return this._cdcService.clientSearch(searchQuery).pipe(map((clients: Client[]) => {
            return new Sale(val, clients[0]);
          }));
        } else  {
          return of(new Sale(val));
        }
      });
      if(obs.length === 0) {
        return of([]);
      }
      return forkJoin(obs).pipe(map((result) => {
        this.sales = result.concat(this.sales);
        this._sales.next(this.sales);
        return result;
      }))
    }));
  }

  public isDeliveryOrder(order: IOrder): boolean{
    return order.orderType === 'order.retailDelivery' ||
    order.orderType === 'order.ecomSpecialDelivery' ||
    order.orderType === 'order.ecomDelivery';
  }

  public isPickupOrder(order: IOrder): boolean{
    return order.orderType === 'order.ecomPickup' || order.orderType === 'order.retailPickup';
  }

  public isTransferOrder(order: IOrder): boolean{
    return order.orderType === 'order.retailTransfer';
  }


  private get _locale(): string {
    if (this._translate.currentLang === 'fr') {
      return 'fr';
    } else {
      return 'en';
    }
  }

  public resetSales(): void {
    this._sales.next([]);
    this.sales = [];
    this._prev = undefined;
    this._next = undefined;
    this.customerIds = [];
    this.dateRange = new FormGroup({start: new FormControl, end: new FormControl});
    this.totalSales = 0;
    this.salesListYAxis = 0;
    this.selectedSale = undefined;
    this.articleId = '';
  }
}
